import React, { useState, useEffect } from 'react';
import {
    Paper,
    Typography,
    Button,
    Stack,
    IconButton,
    TextField,
    InputAdornment,
    useTheme,
    useMediaQuery,
    Alert,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { authService } from '../services/authService';
import { useResponsiveToast } from '../components/Toast/Toast';
import styles from '../components/Login/LoginPage.module.css';
import logo from '../assets/images/logo/logo.png';

const PasswordResetPage: React.FC = () => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { success } = useResponsiveToast();

    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            setError('Invalid or missing reset token. Please check your email link.');
        }
    }, [token]);

    const handlePasswordReset = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!token) {
            setError('Invalid or missing reset token. Please check your email link.');
            return;
        }

        if (!newPassword || !confirmPassword) {
            setError('Please enter both password fields.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match. Please try again.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await authService.resetPassword({
                token,
                new_password: newPassword,
                new_password_confirm: confirmPassword,
            });
            
            success('Password reset successfully! Redirecting to login...');
            
            // Redirect to login page after a short delay
            setTimeout(() => {
                navigate('/login');
            }, 2000);
        } catch (err: any) {
            setError(err.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.loginBg}>
            <Paper elevation={3} className={styles.loginCard}>
                <Stack spacing={isMobile ? 2 : 3} alignItems='center'>
                    <img
                        src={logo}
                        alt='Gensize Logo'
                        style={{
                            width: isMobile ? '120px' : '150px',
                        }}
                    />
                    <Typography variant='h5' className={styles.heading}>
                        Reset Your Password
                    </Typography>
                    <Typography 
                        variant='body2' 
                        sx={{ 
                            textAlign: 'center', 
                            color: '#666',
                            fontSize: isMobile ? '12px' : '14px',
                            fontFamily: 'Poppins, sans-serif',
                        }}
                    >
                        Enter your new password below
                    </Typography>
                    <form onSubmit={handlePasswordReset} className={styles.form} autoComplete='off'>
                        <Stack spacing={isMobile ? 1.5 : 2}>
                            <TextField
                                label='New Password'
                                type={showPassword ? 'text' : 'password'}
                                value={newPassword}
                                onChange={e => {
                                    setNewPassword(e.target.value);
                                    if (error) setError('');
                                }}
                                fullWidth
                                required
                                autoFocus
                                variant='outlined'
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '4px',
                                    },
                                }}
                                slots={{
                                    input: undefined,
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <LockOutlinedIcon
                                                    sx={{ fontSize: isMobile ? 20 : 24 }}
                                                />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <IconButton
                                                    aria-label={
                                                        showPassword
                                                            ? 'Hide password'
                                                            : 'Show password'
                                                    }
                                                    onClick={() => setShowPassword(prev => !prev)}
                                                    edge='end'
                                                    size={isMobile ? 'small' : 'medium'}
                                                    tabIndex={-1}
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff
                                                            sx={{ fontSize: isMobile ? 20 : 24 }}
                                                        />
                                                    ) : (
                                                        <Visibility
                                                            sx={{ fontSize: isMobile ? 20 : 24 }}
                                                        />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            <TextField
                                label='Confirm New Password'
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={e => {
                                    setConfirmPassword(e.target.value);
                                    if (error) setError('');
                                }}
                                fullWidth
                                required
                                variant='outlined'
                                size={isMobile ? 'small' : 'medium'}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '4px',
                                    },
                                }}
                                slots={{
                                    input: undefined,
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position='start'>
                                                <LockOutlinedIcon
                                                    sx={{ fontSize: isMobile ? 20 : 24 }}
                                                />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <IconButton
                                                    aria-label={
                                                        showConfirmPassword
                                                            ? 'Hide password'
                                                            : 'Show password'
                                                    }
                                                    onClick={() => setShowConfirmPassword(prev => !prev)}
                                                    edge='end'
                                                    size={isMobile ? 'small' : 'medium'}
                                                    tabIndex={-1}
                                                >
                                                    {showConfirmPassword ? (
                                                        <VisibilityOff
                                                            sx={{ fontSize: isMobile ? 20 : 24 }}
                                                        />
                                                    ) : (
                                                        <Visibility
                                                            sx={{ fontSize: isMobile ? 20 : 24 }}
                                                        />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    },
                                }}
                            />
                            {error && (
                                <Alert 
                                    severity="error" 
                                    sx={{ 
                                        mt: 1,
                                        '& .MuiAlert-message': {
                                            fontSize: isMobile ? '12px' : '14px',
                                            fontFamily: 'Poppins, sans-serif',
                                        }
                                    }}
                                >
                                    {error}
                                </Alert>
                            )}
                            <Button
                                type='submit'
                                variant='contained'
                                fullWidth
                                className={styles.loginButton}
                                disabled={loading || !token}
                                sx={{ mt: isMobile ? 2 : 3, color: 'white' }}
                            >
                                {loading ? 'Changing Password...' : 'Change Password'}
                            </Button>
                        </Stack>
                    </form>
                </Stack>
            </Paper>
        </div>
    );
};

export default PasswordResetPage;
